<template>
  <div class="weekly-view">
    <div class="week-header">
      <div class="time-column"></div>
      <div 
        v-for="day in weekDays" 
        :key="day.date.toString()"
        class="day-header"
        :class="{ 'today': day.isToday, 'weekend': day.isWeekend }"
      >
        <div class="day-name">{{ day.name }}</div>
        <div class="day-date">{{ day.date.getDate() }}</div>
      </div>
    </div>
    <div class="week-grid">
      <div 
        v-for="timeSlot in timeSlots" 
        :key="timeSlot.period"
        class="time-slot"
      >
        <div class="time-label">{{ timeSlot.label }}</div>
        <div 
          v-for="day in weekDays" 
          :key="day.date.toString()"
          class="day-cell"
          :class="{ 'today': day.isToday, 'weekend': day.isWeekend }"
        >
          <div 
            v-for="session in getSessionsForDayAndPeriod(day.date, timeSlot.period)" 
            :key="session.session_id"
            class="session-item"
            @click="$emit('session-click', session)"
          >
            <div class="session-time">
              {{ formatTime(session.start_at) }} - {{ formatTime(session.end_at) }}
            </div>
            <div class="session-course">{{ session.course.name }}</div>
            <div class="session-status">
              <div 
                v-if="session.absentStudents.length > 0" 
                class="session-absent"
                :class="{ 'all-completed': session.absentStudents.length === session.completedMakeupClassStudents?.length }"
              >
                {{ session.absentStudents.length }} absent
              </div>
              <div 
                v-if="session.completedMakeupClassStudents?.length > 0" 
                class="makeup-completed"
                :title="`${session.completedMakeupClassStudents.length} makeup classes completed`"
              >
                ✓ {{ session.completedMakeupClassStudents.length }}/{{ session.absentStudents.length }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const today = new Date();
const props = defineProps({
  sessions: Array,
  currentDate: Date
});

// Define our 3 time periods
const timeSlots = [
  { period: 'morning', label: 'Morning (8am-12pm)' },
  { period: 'afternoon', label: 'Afternoon (12pm-6pm)' },
  { period: 'evening', label: 'Evening (6pm-10pm)' }
];

const weekDays = computed(() => {
  const currentDate = new Date(props.currentDate);
  currentDate.setHours(0, 0, 0, 0);
  
  // Find Monday of the current week
  const currentDay = currentDate.getDay();
  const startDate = new Date(currentDate);
  startDate.setDate(currentDate.getDate() - (currentDay === 0 ? 6 : currentDay - 1));
  
  return Array.from({ length: 7 }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(startDate.getDate() + i);
    const dayOfWeek = date.getDay();
    
    return {
      date,
      name: date.toLocaleDateString('default', { weekday: 'short' }),
      isToday: date.toDateString() === today.toDateString(),
      isWeekend: dayOfWeek === 0 || dayOfWeek === 6,
    };
  });
});

function getSessionsForDayAndPeriod(day, period) {
  const dayStart = new Date(day);
  const dayEnd = new Date(day);
  
  // Set time ranges based on period
  if (period === 'morning') {
    dayStart.setHours(8, 0, 0, 0);
    dayEnd.setHours(12, 0, 0, 0);
  } else if (period === 'afternoon') {
    dayStart.setHours(12, 0, 0, 0);
    dayEnd.setHours(18, 0, 0, 0);
  } else { // evening
    dayStart.setHours(18, 0, 0, 0);
    dayEnd.setHours(22, 0, 0, 0);
  }

  return props.sessions.filter(session => {
    const sessionStart = new Date(session.start_at);
    const sessionEnd = new Date(session.end_at);
    
    // Filter out sessions outside 8am-10pm
    if (sessionStart.getHours() < 8 || sessionEnd.getHours() >= 22) {
      return false;
    }
    // Check if session falls within the current period
    return sessionStart >= dayStart && sessionStart < dayEnd;
  }).sort((a, b) => {
    return new Date(a.start_at) - new Date(b.start_at);
  });
}

function formatTime(dateString) {
  const date = new Date(dateString);
  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
</script>

<style scoped>
.weekly-view {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.week-header {
  display: grid;
  grid-template-columns: 150px repeat(7, 1fr);
  background: #f5f5f5;
  font-weight: bold;
  text-align: center;
}

.day-header {
  padding: 10px 5px;
  border-left: 1px solid #ddd;
}

.day-header.today {
  background-color: #e6f7ff;
}

.day-name {
  font-size: 0.9rem;
}

.day-date {
  font-size: 1.2rem;
}

.week-grid {
  display: grid;
  grid-template-columns: 150px repeat(7, 1fr);
  grid-auto-rows: 100px;
  flex-grow: 1;
  overflow-y: auto;
}

.time-slot {
  display: contents;
}

.time-label {
  grid-column: 1;
  padding: 10px;
  font-size: 0.9rem;
  text-align: right;
  border-top: 1px solid #eee;
  color: #666;
  display: flex;
  align-items: center;
  justify-content: flex-end;
}

.day-cell {
  position: relative;
  border-top: 1px solid #eee;
  border-left: 1px solid #eee;
  min-height: 100px;
  overflow-y: auto;
}

.day-cell.weekend {
  background-color: #fff0f5;
}

.day-cell.today {
  background-color: #f5fbff;
}

.day-cell.today.weekend {
  background-color: #e6f2ff;
}

.session-item {
  background: var(--primary-color);
  color: white;
  padding: 3px 5px;
  margin-bottom: 3px;
  border-radius: 3px;
  font-size: 0.8rem;
  cursor: pointer;
}

.session-item:hover {
  opacity: 0.9;
}

.session-time {
  font-weight: bold;
  font-size: 0.7rem;
}

.session-course {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-size: 0.8rem;
}

.session-status {
  display: flex;
  gap: 4px;
  margin-top: 2px;
  flex-wrap: wrap;
}

.session-absent {
  font-size: 0.7rem;
  background: var(--danger-color); /* Default red for incomplete */
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
  color: white;
}

.session-absent.all-completed {
  background: #D7A3A3; /* Gray when all makeup classes are done */
  color: #5D1F1F;
}

.makeup-completed {
  font-size: 0.7rem;
  background: var(--secondary-color); /* Blue for completed */
  padding: 1px 3px;
  border-radius: 3px;
  display: inline-block;
  color: white;
}
</style>